import { useFlags, withLDProvider } from "launchdarkly-react-client-sdk";
import { findIndex } from "lodash";
import axios from "axios";
import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { hotjar } from "react-hotjar";
import { useRoutes } from "react-router-dom";
import { ConfirmProvider } from "material-ui-confirm";
import { CssBaseline, ThemeProvider } from "@mui/material";
import DuplicatePatientModal from "./components/DuplicatePatientModal";
import MedicalEncounterModal from "./components/MedicalEncounterModal";
import OrgSelectionModal from "./components/OrgSelectionModal";
import RTL from "./components/RTL";
import SettingsDrawer from "./components/SettingsDrawer";
import SplashScreen from "./components/SplashScreen";
import UserProfileModal from "./components/UserProfileModal";
import { hotjarConfig, ldConfig } from "./config";
import useAuth from "./hooks/useAuth";
import useOrgSelectionModal from "./hooks/useOrgSelectionModal";
import useProfileModal from "./hooks/useProfileModal";
import useScrollReset from "./hooks/useScrollReset";
import useSettings from "./hooks/useSettings";
import "./i18n";
import routes from "./routes";
import {
  getUserProfile,
  getUserRoles,
  setUserOrganization,
  getCurrentOrganization,
} from "./slices/user";
import { useDispatch, useSelector } from "./store";
import { createCustomTheme } from "./theme";
import getUserId from "./utils/getUserId";
import ErrorBoundary from "./ErrorLoadingComponent";
import useRoles from "./hooks/useRoles";
import useMedicalEncounterModal from "./hooks/useMedicalEncounterModal";
import useHealthChecks from "./hooks/useHealthChecks";
import useSocketIO from "./hooks/useSocketIO";
import useGlobalNotifications from "./hooks/useGlobalNotifications";
import { notificationService } from "./services/notificationService";
import { getMedicalConfig } from "./slices/configuration";
import { ScrollProvider } from "./contexts/ScrollContext";
import { NavigationHistoryProvider } from "./contexts/NavigationHistoryContext";
import ChunkErrorBoundary from "./components/errorboundary/ChunkErrorBoundary";
import { getTemplates } from "./slices/template";
import RoleBasedRedirect from "./components/RoleBasedRedirect";

const App = withLDProvider({
  clientSideID: `${ldConfig.clientSideId}`,
  options: {
    bootstrap: "localStorage",
  },
})(() => {
  useHealthChecks();
  useSocketIO();
  useGlobalNotifications(); // Listen for global notifications (like logout alerts)
  const { settings } = useSettings();
  const { selectedOrganization } = settings;

  const auth = useAuth();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);
  const launchDarklyFlags = useFlags();
  const currentOrg = useSelector((state) => state.user.currentOrganization);
  const userRoles = useRoles();
  const { isOpen: medicalEncounterModalIsOpen } = useMedicalEncounterModal();
  const { openUserProfileModal } = useProfileModal();
  const { openOrgSelectionModal, closeOrgSelectionModal } =
    useOrgSelectionModal();

  const [filteredRoutes, setFilteredRoutes] = useState(routes);
  const content = useRoutes(filteredRoutes);

  useScrollReset();

  // Check for post-redirect notifications (like logout due to inactivity)
  useEffect(() => {
    notificationService.checkAndShowPostRedirectNotification();
  }, []);

  useEffect(() => {
    if (launchDarklyFlags && currentOrg && userRoles) {
      // filter routes here based on flags, and org details and roles
      const routesToSet = routes;
      setFilteredRoutes(routesToSet);
    }
  }, [launchDarklyFlags, currentOrg, userRoles]);

  useEffect(() => {
    if (hotjarConfig.id && hotjarConfig.snippetVersion) {
      hotjar.initialize(hotjarConfig.id, hotjarConfig.snippetVersion);
    }
  }, []);

  useEffect(() => {
    const { isAuthenticated, user } = auth;
    if (isAuthenticated && user) {
      const userId = getUserId(user);
      dispatch(getUserProfile(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user, dispatch]);

  useEffect(() => {
    if (profile) {
      if (!profile.name[0]?.firstName) {
        openUserProfileModal();
      }
    }
  }, [openUserProfileModal, profile]);

  useEffect(() => {
    const { organizations } = auth;
    if (organizations && selectedOrganization) {
      const index = findIndex(
        organizations,
        (o) =>
          o.id === selectedOrganization.id ||
          (o.authId &&
            selectedOrganization.authId &&
            o.authId === selectedOrganization.authId) ||
          (o.authId && o.authId === selectedOrganization.id) ||
          (selectedOrganization.authId && o.id === selectedOrganization.authId),
      );
      const found = index > -1;
      if (!found) {
        openOrgSelectionModal();
      } else {
        axios.defaults.headers.common.organization = `Organization/${organizations[index].id}`;
        axios.defaults.headers.common["X-Organization-AuthId"] =
          organizations[index].authId;
        dispatch(setUserOrganization(organizations[index]));
        dispatch(getUserRoles(organizations[index].authId));
        dispatch(getCurrentOrganization(organizations[index].id));
        dispatch(getMedicalConfig(organizations[index].id));
        dispatch(getTemplates());

        closeOrgSelectionModal();
      }
    } else {
      openOrgSelectionModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrganization,
    auth.organizations,
    dispatch,
    openOrgSelectionModal,
    closeOrgSelectionModal,
  ]);

  const theme = createCustomTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
    theme: settings.theme,
  });

  // TODO: Replace null screen with a backsplash for medial health when no org is selected
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <ChunkErrorBoundary>
          <ScrollProvider>
            <NavigationHistoryProvider>
              <ConfirmProvider>
                <RTL direction={settings.direction}>
                  <CssBaseline />
                  <Toaster position="top-center" />
                  <SettingsDrawer />
                  {medicalEncounterModalIsOpen && <MedicalEncounterModal />}
                  <DuplicatePatientModal />
                  <OrgSelectionModal />
                  <UserProfileModal />

                  {/* TODO: replace content with page asking for org */}
                  {auth.isInitialized ? (
                    <RoleBasedRedirect>{content}</RoleBasedRedirect>
                  ) : (
                    <SplashScreen />
                  )}
                </RTL>
              </ConfirmProvider>
            </NavigationHistoryProvider>
          </ScrollProvider>
        </ChunkErrorBoundary>
      </ThemeProvider>
    </ErrorBoundary>
  );
});

export default App;
